import { defineCatalog } from '@rachelallyson/spectra/catalog';
import type { Publisher } from '@rachelallyson/spectra/publishers';

import { adsSchemas } from './ads';
import { ADMIN_LEGACY_POSTHOG_NAMES, adminSchemas } from './admin';
import { BILLING_LEGACY_POSTHOG_NAMES, billingSchemas } from './billing';
import { type CanaryService, canarySchemas } from './canary';
import { chmsSchemas } from './chms';
import { classesSchemas } from './classes';
import { distinctIdFor } from './distinctId';
import { flagsSchemas } from './featureFlags';
import { leadSchemas } from './lead';
import { legacyApiSchemas } from './legacyApi';
import { navigationSchemas } from './navigation';
import { pcoSchemas } from './pco';
import { posthogCapturePublisher } from './posthogPublisher';
import { queueSchemas } from './queue';
import { referralSchemas } from './referral';
import { resourcesSchemas } from './resources';
import {
  SIGNUP_TRIAL_LEGACY_POSTHOG_NAMES,
  signupTrialSchemas,
} from './signupTrial';
import { sweepsSchemas } from './sweeps';
import { trainingSchemas } from './training';
import { customerioSchemas } from './customerio';
import { TURNSTILE_LEGACY_POSTHOG_NAMES, turnstileSchemas } from './turnstile';

// The OnArk event catalog — the contract between code, tests, and monitoring
// (docs/decisions/0002-catalog-driven-observability-testing.md). One catalog
// for the whole product; per-domain schema maps live in their own modules and
// merge here as domains are retrofitted (next: worker job lifecycle, block
// editor).
//
// BROWSER-SAFE ON PURPOSE: this entry imports only spectra subpaths that are
// free of Node builtins (the barrel re-exports createContext → node:async_hooks
// and the test harness → node:fs, which breaks the Next browser bundle — found
// by driving the real page, not by vitest, which runs in Node). Test helpers
// live in '@onark/observability/testing'; server-only publishers get their own
// entry when the worker adopts the catalog.
const rawCatalog = defineCatalog({
  ...adminSchemas,
  ...adsSchemas,
  ...billingSchemas,
  ...canarySchemas,
  ...chmsSchemas,
  ...classesSchemas,
  ...customerioSchemas,
  ...flagsSchemas,
  ...leadSchemas,
  ...legacyApiSchemas,
  ...navigationSchemas,
  ...pcoSchemas,
  ...queueSchemas,
  ...referralSchemas,
  ...resourcesSchemas,
  ...signupTrialSchemas,
  ...sweepsSchemas,
  ...trainingSchemas,
  ...turnstileSchemas,
});

/**
 * Process-wide publisher registry, keyed by a global symbol so every COPY of
 * this module sees it. Next bundles `@onark/observability` (transpilePackages)
 * once per compiled graph, so a router, a Server Component and
 * instrumentation.ts can each hold their own `rawCatalog`; module-local state
 * is exactly how a publisher registered in one went missing in another
 * (#404). Registering here, once per runtime, makes the copies agree.
 */
type SharedState = {
  publishers?: Publisher<typeof rawCatalog.schemas>[];
  reported: Set<string>;
  reporter?: (error: Error) => void;
};

// No @types/node here on purpose (browser-safe entry); bundlers inline NODE_ENV.
declare const process: { env: { NODE_ENV?: string } } | undefined;

const SHARED_KEY = Symbol.for('onark.observability.catalog');

function shared(): SharedState {
  const g = globalThis as { [SHARED_KEY]?: SharedState };

  return (g[SHARED_KEY] ??= { reported: new Set() });
}

/**
 * Register this runtime's publishers. Every runtime calls this once, at its
 * own entry point (instrumentation.ts, instrumentation-client.ts, the api and
 * worker boot) — never as a side effect of importing something.
 */
export function registerCatalogPublishers(
  publishers: Publisher<typeof rawCatalog.schemas>[]
): void {
  shared().publishers = publishers;
  rawCatalog.setPublishers(publishers);
}

/** Where a no-publisher emit is reported in production (Rollbar). */
export function setMissingPublisherReporter(
  reporter: (error: Error) => void
): void {
  shared().reporter = reporter;
}

/**
 * Emitting with no publisher used to validate the payload and fan out to
 * nobody, silently. Now: adopt the runtime's registered publishers if this
 * copy of the catalog has none; otherwise fail loudly — throw under test,
 * report once per event name in production (never throw into a user flow).
 */
function ensurePublisher(name: string): void {
  if (rawCatalog.getPublishers().length > 0) return;

  const state = shared();

  if (state.publishers?.length) {
    rawCatalog.setPublishers(state.publishers);

    return;
  }

  const error = new Error(
    `[observability] "${name}" was emitted but no catalog publisher is registered in this runtime`
  );

  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    throw error;
  }
  if (state.reported.has(name)) return;
  state.reported.add(name);
  (state.reporter ?? console.error)(error);
}

export const catalog: typeof rawCatalog = {
  ...rawCatalog,
  __reset: () => {
    rawCatalog.__reset();
    Object.assign(shared(), {
      publishers: undefined,
      reported: new Set(),
      reporter: undefined,
    });
  },
  emit: (name, payload) => {
    ensurePublisher(String(name));
    rawCatalog.emit(name, payload);
  },
  emitAsync: (name, payload) => {
    ensurePublisher(String(name));

    return rawCatalog.emitAsync(name, payload);
  },
};

export type OnArkEventName = keyof typeof catalog.schemas;

type PayloadOf<N extends OnArkEventName> = (typeof catalog.schemas)[N] extends {
  parse: (input: unknown) => infer T;
}
  ? T
  : never;

/**
 * `catalog.emit` in strict mode throws a ZodError at the CALLER on a bad
 * payload. That is the right default — a wrong payload should fail loudly in
 * dev and test. But on a path that mutates account state mid-loop (the
 * lifecycle sweeps close and wall churches one at a time), a throw from the
 * telemetry line would abandon the rest of the run *after* some churches had
 * already been changed, turning a reporting bug into an operational one.
 *
 * So: on those paths, emit through here. A dropped event is logged and the
 * loop carries on. Publisher failures are already contained by spectra itself
 * (`onPublisherError`); this covers validation only.
 */
export function safeEmit<N extends OnArkEventName>(
  name: N,
  payload: PayloadOf<N>
): void {
  try {
    catalog.emit(name, payload);
  } catch (error) {
    console.error(
      `[observability] dropped ${String(name)} — payload failed validation`,
      error
    );
  }
}

/**
 * Point the catalog at a server runtime's PostHog client, once.
 *
 * Both Node runtimes — the worker and the Koa API — need the identical wiring:
 * wrap their own `capture` in the PostHog publisher, resolve identity via
 * `distinctIdFor`, install it as the sole publisher. They had grown two copies
 * of that (jscpd caught the second one), which is worth avoiding for a reason
 * beyond tidiness: if the two ever disagree on identity, the same church
 * silently becomes two people in PostHog and every per-church funnel splits.
 *
 * Each app still brings its own `capture` — the two publish to DIFFERENT
 * PostHog projects (worker → 10583 "OnArk - Automations", API → 10465 "OnArk -
 * App") and stamp their own environment label. That difference is deliberate
 * and stays with the caller; only the wiring is shared.
 *
 * Returns an idempotent init: a second call is a no-op, so importing it from
 * more than one entry point cannot double-publish.
 *
 * The init also emits one `observability.catalog_wired` canary through the
 * publisher it just installed (see ./canary.ts): the API's only catalog
 * vocabulary is the referral money path, so without a boot event "no
 * referrals" and "publisher never installed" produce the same silence in
 * PostHog. Emitted via `safeEmit` — a canary bug must never take the boot
 * path down with it.
 */
export function createPosthogCatalogWiring(
  capture: (args: {
    distinctId: string;
    event: string;
    properties?: Record<string, unknown>;
  }) => void,
  service: CanaryService
): () => void {
  let wired = false;

  return function wire(): void {
    if (wired) return;
    wired = true;

    registerCatalogPublishers([
      posthogCapturePublisher<typeof catalog.schemas>((event, properties) => {
        capture({
          distinctId: distinctIdFor(properties),
          event,
          properties,
        });
      }),
    ]);

    safeEmit('observability.catalog_wired', { service });
  };
}

export { ADMIN_LEGACY_POSTHOG_NAMES, adminSchemas } from './admin';
export { LEGACY_API_FAILURE_REASONS } from './legacyApi';
export type { LegacyApiFailureReason } from './legacyApi';
export { CHMS_CALLERS } from './chms';
export type { ChmsCaller } from './chms';
export {
  BILLING_CANCEL_FAILURE_REASONS,
  BILLING_CHURN_RECORD_FAILURE_REASONS,
  BILLING_LEGACY_POSTHOG_NAMES,
  billingSchemas,
} from './billing';
export type {
  BillingCancelFailureReason,
  BillingChurnRecordFailureReason,
} from './billing';
export { CANARY_SERVICES, canarySchemas } from './canary';
export type { CanaryService } from './canary';
export {
  CHECKIN_FAILURE_REASONS,
  CHECKIN_VIA,
  classesSchemas,
  COMPLETION_CTA_DESTINATIONS,
  COURSE_PUBLISH_BLOCKED_REASONS,
  COURSE_PUBLISH_FAILURE_REASONS,
  PUBLISHABLE_SUBSCRIPTION_STATUSES,
  RUN_COMPLETION_FAILURE_STAGES,
  RUN_COMPLETION_SOURCES,
} from './classes';
export type {
  CheckinFailureReason,
  CompletionCtaDestination,
  CoursePublishBlockedReason,
  CoursePublishFailureReason,
  RunCompletionFailureStage,
  RunCompletionSource,
} from './classes';
export { distinctIdFor } from './distinctId';
export {
  createFeatureFlagResolver,
  fallbackFeatureFlagSnapshot,
  FEATURE_FLAG_FAILURE_REASONS,
  FEATURE_FLAGS,
  featureFlagOverridesFromEnv,
  flagsSchemas,
  isFeatureFlagKey,
  parseFeatureFlagOverrides,
  posthogFeatureFlagEvaluate,
} from './featureFlags';
export type {
  FeatureFlagDefinition,
  FeatureFlagEvaluation,
  FeatureFlagFailureReason,
  FeatureFlagKey,
  FeatureFlagOverrides,
  FeatureFlagResolver,
  FeatureFlagResolverOptions,
  FeatureFlagSnapshot,
  ParsedFeatureFlagOverrides,
} from './featureFlags';
export {
  REFERRAL_SKIP_REASONS,
  REFERRAL_STAMP_SOURCES,
  REFERRAL_TRIGGERS,
  referralSchemas,
} from './referral';
export type {
  ReferralSkipReason,
  ReferralStampSource,
  ReferralTrigger,
} from './referral';
export { adsSchemas, OFFLINE_CONVERSION_SKIP_REASONS } from './ads';
export type { OfflineConversionSkipReason } from './ads';
export {
  SIGNUP_TRIAL_FAILURE_REASONS,
  signupTrialSchemas,
  TRIAL_START_FAILURE_REASONS,
} from './signupTrial';
export type {
  SignupTrialFailureReason,
  SignupTrialServerFailureReason,
  TrialStartFailureReason,
} from './signupTrial';
export {
  SWEEP_FAILURE_REASONS,
  SWEEP_NAMES,
  SWEEP_STAGES,
  sweepsSchemas,
  TERMINAL_SWEEP_STAGES,
} from './sweeps';
export type { SweepFailureReason, SweepName, SweepStage } from './sweeps';
export {
  TRAINING_CHECK_IN_VIAS,
  TRAINING_FUNNEL_CLASSES,
  trainingSchemas,
} from './training';
export type { TrainingFunnelClass } from './training';
export {
  CUSTOMERIO_WRITE_FAILURE_REASONS,
  CUSTOMERIO_WRITE_TARGETS,
  customerioSchemas,
} from './customerio';
export type {
  CustomerioWriteFailureReason,
  CustomerioWriteTarget,
} from './customerio';
export { TURNSTILE_LEGACY_POSTHOG_NAMES, turnstileSchemas } from './turnstile';
export {
  PCO_AUTOMATION_BREAKAGES,
  PCO_ERROR_SITES,
  PCO_DUPLICATE_LOOKUPS,
  PCO_DUPLICATE_RECOVERY_STRATEGIES,
  PCO_FIELD_FAILURE_REASONS,
  PCO_FIELD_HANDLERS,
  pcoSchemas,
} from './pco';
export type {
  PcoAutomationBreakage,
  PcoDuplicateLookup,
  PcoDuplicateRecoveryStrategy,
  PcoErrorSite,
  PcoFieldFailureReason,
  PcoFieldHandler,
} from './pco';
export {
  RESOURCE_COPY_FAILURE_REASONS,
  RESOURCE_TEMPLATE_KEYS,
  resourcesSchemas,
} from './resources';
export type {
  ResourceCopyFailureReason,
  ResourceTemplateKey,
} from './resources';
export { QUEUE_ROUTERS, queueSchemas } from './queue';
export type { QueueRouter } from './queue';
export { posthogCapturePublisher } from './posthogPublisher';
export type { Publisher } from '@rachelallyson/spectra/publishers';

/** Merged rename map for every domain that still has pre-catalog PostHog names. */
export const LEGACY_POSTHOG_NAMES: Readonly<
  Partial<Record<OnArkEventName, string>>
> = {
  ...ADMIN_LEGACY_POSTHOG_NAMES,
  ...BILLING_LEGACY_POSTHOG_NAMES,
  ...SIGNUP_TRIAL_LEGACY_POSTHOG_NAMES,
  ...TURNSTILE_LEGACY_POSTHOG_NAMES,
};
